import {
    FinancialAccount,
    FinancialAccountSourceType,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import {
    ConnectionProvider,
    IntegrationConnection,
} from "@common/types/integrationConnection";
import { isPlaidConnection } from "@common/helpers/financialConnection";
import {
    integrationAccountSyncsStatements,
    isIntegrationConnection,
} from "@common/helpers/integrationConnection";

type AccountIntegrationAccount = NonNullable<
    FinancialAccount["integrationAccounts"]
>[number];

export type StatementSyncState =
    | "not-qualified"
    | "not-supported"
    | "supported-not-enabled"
    | "enabled-not-synced"
    | "synced-none-found"
    | "synced-found";

export interface StatementSyncInfo {
    state: StatementSyncState | null;
    lastStatementDate: string | null;
    statementsSyncedAt: Date | null;
}

const EMPTY_STATEMENT_SYNC_INFO: StatementSyncInfo = {
    state: null,
    lastStatementDate: null,
    statementsSyncedAt: null,
};

function getStatementSyncState({
    statementsSupported,
    statementsEnabled,
    statementsSyncedAt,
    lastStatementDate,
}: {
    statementsSupported: boolean | null;
    statementsEnabled: boolean | null;
    statementsSyncedAt: Date | null;
    lastStatementDate: string | null;
}): StatementSyncState | null {
    if (statementsSupported === null && !lastStatementDate) {
        return null;
    }

    if (statementsSupported === false) {
        return "not-supported";
    }

    if (statementsSupported && !statementsEnabled) {
        return "supported-not-enabled";
    }

    if (statementsEnabled && !statementsSyncedAt) {
        return "enabled-not-synced";
    }

    if (statementsSyncedAt && !lastStatementDate) {
        return "synced-none-found";
    }

    if (statementsSyncedAt && lastStatementDate) {
        return "synced-found";
    }

    return null;
}

export function getAccountStatementSyncState({
    account,
    isPlaidStatementsQualified,
}: {
    account: FinancialAccount;
    isPlaidStatementsQualified: boolean;
}): StatementSyncInfo {
    if (account.sourceType === FinancialAccountSourceType.PLAID) {
        return resolvePlaidStatementSyncInfo({
            integrationAccount: account.integrationAccounts?.find(
                (ia) => ia.connection && isPlaidConnection(ia.connection),
            ),
            isPlaidStatementsQualified,
        });
    }

    const integrationAccount = account.integrationAccounts?.find(
        (ia) =>
            ia.integrationConnection &&
            integrationAccountSyncsStatements({
                provider: ia.integrationConnection.provider,
                financialType: account.financialType,
            }),
    );
    if (!integrationAccount?.integrationConnection) {
        return EMPTY_STATEMENT_SYNC_INFO;
    }

    return resolveIntegrationStatementSyncInfo({
        integrationAccount,
        provider: integrationAccount.integrationConnection.provider,
        financialType: account.financialType,
    });
}

export function getConnectionStatementSyncState({
    account,
    connection,
    isPlaidStatementsQualified,
}: {
    account: FinancialAccount;
    connection: FinancialConnection | IntegrationConnection;
    isPlaidStatementsQualified: boolean;
}): StatementSyncInfo {
    if (isIntegrationConnection(connection)) {
        return resolveIntegrationStatementSyncInfo({
            integrationAccount: account.integrationAccounts?.find(
                (ia) => ia.integrationConnectionId === connection.id,
            ),
            provider: connection.provider,
            financialType: account.financialType,
        });
    }

    if (!isPlaidConnection(connection)) {
        return EMPTY_STATEMENT_SYNC_INFO;
    }

    return resolvePlaidStatementSyncInfo({
        integrationAccount: account.integrationAccounts?.find(
            (ia) =>
                ia.connectionId === connection.id &&
                ia.connection &&
                isPlaidConnection(ia.connection),
        ),
        isPlaidStatementsQualified,
    });
}

function resolvePlaidStatementSyncInfo({
    integrationAccount,
    isPlaidStatementsQualified,
}: {
    integrationAccount: AccountIntegrationAccount | undefined;
    isPlaidStatementsQualified: boolean;
}): StatementSyncInfo {
    if (!isPlaidStatementsQualified) {
        return { ...EMPTY_STATEMENT_SYNC_INFO, state: "not-qualified" };
    }

    if (
        !integrationAccount?.connection ||
        !isPlaidConnection(integrationAccount.connection)
    ) {
        return EMPTY_STATEMENT_SYNC_INFO;
    }

    const lastStatementDate = integrationAccount.lastStatementDate ?? null;
    const statementsSyncedAt = integrationAccount.statementsSyncedAt ?? null;

    return {
        state: getStatementSyncState({
            statementsSupported:
                integrationAccount.connection.statementsApiSupported,
            statementsEnabled:
                integrationAccount.connection.statementsApiEnabled,
            statementsSyncedAt,
            lastStatementDate,
        }),
        lastStatementDate,
        statementsSyncedAt,
    };
}

function resolveIntegrationStatementSyncInfo({
    integrationAccount,
    provider,
    financialType,
}: {
    integrationAccount: AccountIntegrationAccount | undefined;
    provider: ConnectionProvider;
    financialType: FinancialAccountType;
}): StatementSyncInfo {
    if (
        !integrationAccount ||
        !integrationAccountSyncsStatements({ provider, financialType })
    ) {
        return EMPTY_STATEMENT_SYNC_INFO;
    }

    const lastStatementDate = integrationAccount.lastStatementDate ?? null;
    const statementsSyncedAt = integrationAccount.statementsSyncedAt ?? null;

    return {
        state: getStatementSyncState({
            statementsSupported: true,
            statementsEnabled: true,
            statementsSyncedAt,
            lastStatementDate,
        }),
        lastStatementDate,
        statementsSyncedAt,
    };
}
